import { cn } from "../../lib/utils";

// Left-panel previews for each onboarding step. All content is illustrative
// sample data that reacts to the user's answers; nothing here is real yet.

export type PreviewAnswers = {
  roles: string[];
  jobTypes: string[];
  locations: string[];
  careerStage: string | null;
  school: string;
};

const CARD = "rounded-[14px] border border-melt-hair bg-melt-paper shadow-[0_1px_0_rgba(92,52,23,.04),0_18px_36px_-22px_rgba(92,52,23,.45)]";
const MONO_LABEL = "font-plex text-[10.5px] font-medium uppercase leading-none tracking-[.1em] text-melt-text3";
const DATE = "font-plex text-[10.5px] leading-none tracking-[.06em] text-melt-text3";
const TAG = "rounded-md px-[9px] py-[3px] text-[11px] font-medium";

const TAG_STYLES = {
  applied: "bg-[#F2EDE4] text-melt-text1",
  interview: "bg-melt-peach text-[#8A5210]",
  offer: "bg-[#EAF4EF] text-[#2F6B54]",
  outline: "border border-melt-text1/[.14] text-melt-text2",
};

const COMPANIES = ["Stripe", "Linear", "Ramp"];

function AppCard({
  company,
  role,
  tag,
  tagStyle,
  location,
  date,
  className,
  style,
}: {
  company: string;
  role: string;
  tag: string;
  tagStyle: string;
  location?: string;
  date?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div className={cn(CARD, "w-[236px] px-[15px] py-3.5", className)} style={style}>
      <div className="flex items-baseline gap-2">
        <span className="mr-auto text-[15px] font-medium">{company}</span>
        {date && <span className={DATE}>{date}</span>}
      </div>
      <div className="mt-[3px] text-[13px] text-melt-text2">{role}</div>
      <div className="mt-[11px] flex flex-wrap gap-1.5">
        <span className={cn(TAG, tagStyle)}>{tag}</span>
        {location && <span className={cn(TAG, TAG_STYLES.outline)}>{location}</span>}
      </div>
    </div>
  );
}

function PersonCard({ initial, name, detail, className, style }: { initial: string; name: string; detail: string; className?: string; style?: React.CSSProperties }) {
  return (
    <div className={cn(CARD, "flex w-[260px] items-center gap-[11px] px-3.5 py-3", className)} style={style}>
      <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-[#EBD3B3] text-[13px] font-semibold">
        {initial}
      </span>
      <span>
        <b className="block text-sm font-medium">{name}</b>
        <small className="text-xs text-melt-text2">{detail}</small>
      </span>
    </div>
  );
}

// Suffix for sample role titles based on the first chosen job type.
function roleSuffix(jobTypes: string[]) {
  const t = jobTypes[0];
  if (t === "Internship") return " Intern";
  if (t === "Co-op") return " Co-op";
  if (t === "New grad") return ", New Grad";
  return "";
}

// Step 1: a floating collage of what Caramel does.
function WelcomePreview() {
  const float = "absolute animate-bob motion-reduce:animate-none";
  return (
    <div className="relative h-[420px] w-[520px] max-[980px]:scale-[.8]">
      <AppCard company="Stripe" date="JAN 06" role="Backend Engineer Intern" tag="Interview" tagStyle={TAG_STYLES.interview} location="Seattle" className={cn(float, "left-2.5 top-[30px]")} />
      <PersonCard initial="M" name="Maya Chen" detail="Recruiter at Linear" className={cn(float, "right-0 top-0")} style={{ animationDelay: "-2s" }} />
      <div className={cn(CARD, float, "right-5 top-[150px] w-[230px] px-[15px] py-3.5")} style={{ animationDelay: "-4s" }}>
        <div className={MONO_LABEL}>Interview prep</div>
        <div className="mt-2 text-sm font-medium">Stripe · System design</div>
        <div className="mt-2.5 h-1.5 overflow-hidden rounded-md bg-melt-track">
          <i className="block h-full w-[60%] rounded-md bg-melt-caramel" />
        </div>
      </div>
      <div className={cn(CARD, float, "left-10 top-[215px] flex w-[250px] items-center gap-2.5 px-3.5 py-3 text-[13.5px]")} style={{ animationDelay: "-1s" }}>
        <span className="h-[18px] w-[18px] rounded-md border-[1.5px] border-melt-text1/30" />
        Follow up with Ramp
        <span className={cn(DATE, "ml-auto")}>THU</span>
      </div>
      <AppCard company="Linear" date="DEC 12" role="Design Engineer Intern" tag="Offer" tagStyle={TAG_STYLES.offer} location="Remote" className={cn(float, "left-[170px] top-[300px]")} style={{ animationDelay: "-3s" }} />
    </div>
  );
}

// Step 2: role cards that follow the chosen roles, type and locations.
function RolesPreview({ answers }: { answers: PreviewAnswers }) {
  const roles = answers.roles.length ? answers.roles : ["Software Engineering"];
  const locations = answers.locations.length ? answers.locations : ["Remote", "Toronto", "New York"];
  const suffix = roleSuffix(answers.jobTypes);
  return (
    <div className="flex w-[300px] flex-col gap-3">
      <div className="px-1">
        <span className={MONO_LABEL}>Roles for you</span>
      </div>
      {COMPANIES.map((company, i) => (
        <AppCard
          key={company}
          company={company}
          role={roles[i % roles.length] + suffix}
          tag={answers.jobTypes[0] || "Open role"}
          tagStyle={TAG_STYLES.applied}
          location={locations[i % locations.length]}
          className="w-full"
        />
      ))}
    </div>
  );
}

// Step 3: alumni (or people) at target companies.
function PeoplePreview({ answers }: { answers: PreviewAnswers }) {
  const inSchool = answers.careerStage === "Student" || answers.careerStage === "Recent Graduate";
  const school = answers.school || (inSchool ? "your school" : "");
  const people = [
    ["A", "Amir Haddad", school ? `${school} '24 · SWE at Stripe` : "Engineer at Stripe"],
    ["J", "Julia Park", school ? `${school} '23 · PM at Linear` : "Recruiter at Linear"],
    ["D", "Dev Patel", school ? `${school} '25 · Intern at Ramp` : "Engineer at Ramp"],
  ];
  return (
    <div className="flex w-[300px] flex-col gap-3">
      <div className="px-1">
        <span className={MONO_LABEL}>{school ? "Alumni to reach out to" : "People to reach out to"}</span>
      </div>
      {people.map(([initial, name, detail]) => (
        <PersonCard key={name} initial={initial} name={name} detail={detail} className="w-full" />
      ))}
    </div>
  );
}

// Step 4: a mini board that fills in card by card.
const BUILD_COLUMNS = [
  { name: "Applied", color: "#C9A06B", cards: [["Citadel", "Internship"], ["Cogna", "SWE Intern"], ["Persona", "SWE Intern"]] },
  { name: "Interview", color: "#D98829", cards: [["Together AI", "SWE Intern"]] },
  { name: "Rejected", color: "#C9795A", cards: [["Hitachi", "Analyst Intern"], ["Superhuman", "SWE Intern"]] },
];
export const BUILD_CARD_COUNT = BUILD_COLUMNS.reduce((n, c) => n + c.cards.length, 0);

function SetupPreview({ shown }: { shown: number }) {
  let index = 0;
  return (
    <div className="grid grid-cols-[repeat(3,150px)] gap-2.5">
      {BUILD_COLUMNS.map((col) => (
        <div key={col.name} className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5 pl-0.5 text-xs font-semibold">
            <i className="h-[7px] w-[7px] rounded-[3px]" style={{ background: col.color }} />
            {col.name}
          </div>
          {col.cards.map(([company, role]) => {
            const visible = index++ < shown;
            return (
              <div
                key={company}
                className={cn(
                  CARD,
                  "px-[11px] py-2.5 text-[12.5px] transition-[opacity,transform] duration-[400ms]",
                  visible ? "opacity-100" : "translate-y-2 opacity-0"
                )}
              >
                <b className="block font-medium">{company}</b>
                <small className="text-[11.5px] text-melt-text2">{role}</small>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function OnboardingPreviews({
  step,
  answers,
  buildShown,
}: {
  step: number;
  answers: PreviewAnswers;
  buildShown: number;
}) {
  const previews = [
    <WelcomePreview />,
    <RolesPreview answers={answers} />,
    <PeoplePreview answers={answers} />,
    <SetupPreview shown={buildShown} />,
  ];

  // All previews stay mounted and crossfade (opacity + 14px rise).
  return (
    <div className="relative flex-1">
      {previews.map((preview, i) => (
        <div
          key={i}
          aria-hidden={i !== step}
          className={cn(
            "pointer-events-none absolute inset-0 grid place-items-center transition-[opacity,transform] duration-[450ms] ease-in-out",
            i === step ? "opacity-100" : "translate-y-3.5 opacity-0"
          )}
        >
          {preview}
        </div>
      ))}
    </div>
  );
}
